// 📁 src/OrderHistoryPage.js
import React, { useEffect, useState } from "react";
import { useUser } from "./UserContext";
import { api } from "./axiosClient";

const OrderHistoryPage = () => {
  const { user } = useUser();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    const fetchOrders = async () => {
      // ✅ If user not ready, stop loading gracefully (avoid infinite loader / blank states)
      if (!user?._id) {
        setLoading(false);
        return;
      }

      try {
        setErrMsg("");
        // ✅ Use api client (has correct baseURL and auth header)
        const res = await api.get("/api/orders/my");
        setOrders(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        console.error("Error fetching orders:", error);
        const msg =
          error?.response?.data?.message ||
          `Failed to fetch orders (status: ${error?.response?.status || "?"})`;
        setErrMsg(msg);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [user?._id]);

  const getSymbol = (currency) => {
    switch (currency) {
      case "INR":
        return "₹";
      case "USD":
        return "$";
      case "EUR":
        return "€";
      case "GBP":
        return "£";
      case "AED":
        return "د.إ";
      default:
        return currency ? currency + " " : "";
    }
  };

  // ✅ Safe number formatter (prevents blank page crashes)
  const money = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n.toFixed(2) : "0.00";
  };

  if (loading) {
    return <div className="text-center mt-10 text-[#105132]">Loading your orders...</div>;
  }

  if (!user?._id) {
    return (
      <div className="min-h-screen bg-[#f3f0e4] px-4 sm:px-10 py-10 text-[#105132]">
        <h1 className="text-3xl font-bold mb-4 text-center">Your Order History</h1>
        <p className="text-center text-[#105132]/70">
          Please login to view your orders.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f3f0e4] px-4 sm:px-10 py-10 text-[#105132]">
      <h1 className="text-3xl font-bold mb-8 text-center">Your Order History</h1>

      {errMsg && (
        <div className="max-w-2xl mx-auto mb-6 bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl">
          {errMsg}
        </div>
      )}

      {orders.length === 0 ? (
        <p className="text-center text-[#105132]/70">
          You haven't placed any orders yet.
        </p>
      ) : (
        <div className="space-y-6">
          {orders.map((order) => {
            const symbol = getSymbol(order.currency);

            const total = order.totalConverted ?? order.total;
            const subtotal = order.subtotalConverted ?? order.subtotal;
            const discount = order.discountConverted ?? order.discount;
            const delivery = order.deliveryConverted ?? order.delivery;

            return (
              <div key={order._id} className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-gray-500">Order ID: {order._id}</span>
                  <span className="text-sm text-gray-500">
                    {order.createdAt ? new Date(order.createdAt).toLocaleString() : ""}
                  </span>
                </div>

                <div className="font-semibold mb-2">Name: {order.fullName}</div>
                <div className="text-sm mb-2">
                  Address: {order.address}, {order.city}, {order.state}, {order.zip}
                </div>
                <div className="text-sm mb-2 text-gray-600">
                  Phone: {order.phone} | Email: {order.email}
                </div>

                <div className="text-sm mb-4 text-gray-600">
                  Status: <span className="font-medium text-[#105132]">{order.status}</span>
                  {order.couponCode && (
                    <span className="ml-4 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                      Coupon: {order.couponCode}
                    </span>
                  )}
                </div>

                <div className="border-t pt-2">
                  {(order.cart || []).map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span>
                        {item.name} × {item.quantity}
                      </span>
                      <span>
                        {symbol}
                        {money(Number(item.price) * Number(item.quantity))}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="text-sm text-gray-600 border-t pt-2 mt-2 space-y-1">
                  <div className="flex justify-between">
                    <span>Subtotal</span>
                    <span>
                      {symbol}
                      {money(subtotal)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Discount</span>
                    <span>
                      - {symbol}
                      {money(discount)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Delivery</span>
                    <span>
                      {symbol}
                      {money(delivery)}
                    </span>
                  </div>
                </div>

                <div className="flex justify-between font-bold text-base border-t pt-2 mt-2">
                  <span>Total</span>
                  <span>
                    {symbol}
                    {money(total)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OrderHistoryPage;
